/**
 * Shipping benefit bar + announcement.
 *
 * Shows how far the customer is from free shipping (NL) or a shipping discount (BE/DE),
 * and keeps the message in sync with the country the visitor is actually browsing from.
 *
 * The market country from Liquid is used for the first paint so there is no flash of the
 * wrong message; `/browsing_context_suggestions.json` then refines it for visitors outside
 * the primary market. Configuration comes from the "Verzendvoordeel" theme settings.
 */
(function () {
  'use strict';

  var configElement = document.querySelector('[data-shipping-benefit-config]');
  if (!configElement) return;

  var CONFIG;
  try {
    CONFIG = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }
  if (!CONFIG || !CONFIG.enabled) return;

  var COUNTRY_CACHE_KEY = 'ss-shipping-country';

  function parseCountryList(value) {
    return String(value || '')
      .split(',')
      .map(function (code) {
        return code.trim().toUpperCase();
      })
      .filter(Boolean);
  }

  var FREE_COUNTRIES = parseCountryList(CONFIG.freeCountries);
  var DISCOUNT_COUNTRIES = parseCountryList(CONFIG.discountCountries);

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents)) return '0';
    var parts = (cents / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
    return whole + (parts[1] ? (decimal || '.') + parts[1] : '');
  }

  function formatMoney(cents) {
    var format = CONFIG.moneyFormat || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var amount;

    switch (match && match[1]) {
      case 'amount_no_decimals':
        amount = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        amount = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholder, amount);
  }

  /** Drops the ",00" cents from a whole euro amount so messages stay short. */
  function formatAmount(cents) {
    var money = formatMoney(cents);
    return cents % 100 === 0 ? money.replace(/[.,]00\b/, '') : money;
  }

  function fillPlaceholders(template, values) {
    return String(template || '')
      .replace(/\[bedrag\]/gi, values.bedrag)
      .replace(/\[drempel\]/gi, values.drempel)
      .replace(/\[korting\]/gi, values.korting);
  }

  function tierForCountry(country) {
    // A missing or zero threshold means the setting is incomplete, so show nothing at all
    // rather than claiming the goal is already reached.
    if (!(CONFIG.threshold > 0)) return null;
    if (FREE_COUNTRIES.indexOf(country) !== -1) return 'free';
    if (DISCOUNT_COUNTRIES.indexOf(country) !== -1) return 'discount';
    return null;
  }

  var marketCountry = String(CONFIG.marketCountry || '').toUpperCase();
  var detectedCountry = marketCountry;
  var countryPromise = null;

  function resolveCountry() {
    if (countryPromise) return countryPromise;

    countryPromise = new Promise(function (resolve) {
      var cached = null;
      try {
        cached = window.sessionStorage.getItem(COUNTRY_CACHE_KEY);
      } catch (error) {
        /* storage unavailable, fall through to the request */
      }

      if (cached) {
        detectedCountry = cached;
        resolve(cached);
        return;
      }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(root + 'browsing_context_suggestions.json')
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          var country =
            data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
          country = String(country || marketCountry).toUpperCase();
          try {
            window.sessionStorage.setItem(COUNTRY_CACHE_KEY, country);
          } catch (error) {
            /* storage unavailable, the country is still used for this page */
          }
          detectedCountry = country;
          resolve(country);
        })
        .catch(function () {
          resolve(marketCountry);
        });
    });

    return countryPromise;
  }

  function subscribeToCart(handler) {
    var unsubscribers = [];

    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      var unsubscribe = window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        function (payload) {
          if (payload && payload.cart && typeof payload.cart.total_price === 'number') {
            handler(payload.cart.total_price);
          }
        }
      );
      if (typeof unsubscribe === 'function') unsubscribers.push(unsubscribe);
    }

    function onCartUpdated(event) {
      var cart = event && event.detail && event.detail.cart;
      if (cart && typeof cart.total_price === 'number') handler(cart.total_price);
    }

    document.addEventListener('cart:updated', onCartUpdated);
    unsubscribers.push(function () {
      document.removeEventListener('cart:updated', onCartUpdated);
    });

    return function () {
      unsubscribers.forEach(function (fn) {
        fn();
      });
    };
  }

  class ShippingBenefitBarElement extends HTMLElement {
    connectedCallback() {
      this.total = parseInt(this.getAttribute('data-cart-total'), 10) || 0;
      this.messageElement = this.querySelector('[data-shipping-benefit-message]');
      this.trackElement = this.querySelector('[data-shipping-benefit-track]');
      this.fillElement = this.querySelector('[data-shipping-benefit-fill]');

      this.render();
      resolveCountry().then(this.render.bind(this));

      this.unsubscribe = subscribeToCart(
        function (total) {
          this.total = total;
          this.render();
        }.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    render() {
      var tier = tierForCountry(detectedCountry);

      if (!tier) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      var threshold = CONFIG.threshold;
      var remaining = Math.max(threshold - this.total, 0);
      var reached = remaining <= 0;
      var messages = CONFIG.messages || {};

      var template = reached
        ? tier === 'free'
          ? messages.freeReached
          : messages.discountReached
        : tier === 'free'
        ? messages.freeProgress
        : messages.discountProgress;

      var text = fillPlaceholders(template, {
        bedrag: formatAmount(remaining),
        drempel: formatAmount(threshold),
        korting: formatAmount(CONFIG.discount)
      });

      this.setAttribute('data-state', reached ? 'reached' : 'progress');
      this.setAttribute('data-tier', tier);

      if (this.messageElement && this.messageElement.textContent !== text) {
        this.messageElement.textContent = text;
      }

      if (this.trackElement) {
        this.trackElement.hidden = !CONFIG.showProgress;
      }

      if (this.fillElement) {
        var percentage = threshold > 0 ? Math.min((this.total / threshold) * 100, 100) : 100;
        this.fillElement.style.width = percentage + '%';
        if (this.trackElement) {
          this.trackElement.setAttribute('aria-valuenow', String(Math.round(percentage)));
        }
      }
    }
  }

  class ShippingBenefitAnnouncementElement extends HTMLElement {
    connectedCallback() {
      this.messageElement = this.querySelector('[data-shipping-benefit-message]') || this;
      this.render();
      resolveCountry().then(this.render.bind(this));
    }

    render() {
      var tier = tierForCountry(detectedCountry);

      if (!tier) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      var messages = CONFIG.messages || {};
      var text = fillPlaceholders(tier === 'free' ? messages.announceFree : messages.announceDiscount, {
        bedrag: formatAmount(CONFIG.threshold),
        drempel: formatAmount(CONFIG.threshold),
        korting: formatAmount(CONFIG.discount)
      });

      if (this.messageElement.textContent !== text) {
        this.messageElement.textContent = text;
      }
    }
  }

  if (!customElements.get('shipping-benefit-bar')) {
    customElements.define('shipping-benefit-bar', ShippingBenefitBarElement);
  }
  if (!customElements.get('shipping-benefit-announcement')) {
    customElements.define('shipping-benefit-announcement', ShippingBenefitAnnouncementElement);
  }
})();
